import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  useStripe,
  useElements,
  Elements
} from '@stripe/react-stripe-js';

// Test Stripe key - use your .env in production
const stripePromise = loadStripe('pk_test_51QKJMyRrO6gI32MSOCfKMnVFQ3u9JIHrpyqFCkPzmJ9ELvLYhOGChdtknd8fWIHYYpV4ZWN4wjq6t4DzQOkjJAtH00rSnFc1vS');

// Payment form component
function PaymentForm({ selectedTier, billingCycle, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  const token = localStorage.getItem('token') || localStorage.getItem('saints_token');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;
    if (!token) {
      setError('Please login first');
      return;
    }
    
    setProcessing(true);
    setError(null);
    
    try {
      // 1. Create payment intent
      const response = await fetch('https://saintsacademy.co.za/api/v2/payments/create-intent', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tier: selectedTier.name,
          billingCycle: billingCycle
        })
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Payment failed');
      }
      
      // 2. Confirm payment
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        data.clientSecret, {
          payment_method: { card: cardElement }
        }
      );
      
      if (stripeError) throw new Error(stripeError.message);
      
      // 3. Success
      onSuccess({
        paymentId: paymentIntent.id,
        amount: data.amount,
        tier: selectedTier.display_name
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
      <h3>Payment Details (Test Mode)</h3>
      <p><strong>Test Card:</strong> 4242 4242 4242 4242</p>
      <p><strong>Expiry:</strong> Any future date | <strong>CVC:</strong> Any 3 digits</p>
      
      <div style={{ 
        padding: '12px', 
        background: 'white', 
        border: '1px solid #ccc', 
        borderRadius: '4px',
        margin: '15px 0'
      }}>
        <CardElement />
      </div>
      
      {error && (
        <div style={{ color: '#dc3545', margin: '10px 0', padding: '10px', background: '#f8d7da', borderRadius: '4px' }}>
          ❌ {error}
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '10px' }}>
        <button 
          type="submit"
          disabled={processing || !stripe}
          style={{ 
            padding: '12px 24px',
            background: processing ? '#ccc' : '#6772e5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: processing ? 'not-allowed' : 'pointer'
          }}
        >
          {processing ? 'Processing...' : `Pay $${selectedTier.monthly_price * 0.055}`}
        </button>
        
        <button 
          type="button"
          onClick={onCancel}
          style={{ 
            padding: '12px 24px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// Main test component
function PaymentTest() {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [status, setStatus] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [message, setMessage] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  
  const token = localStorage.getItem('token') || localStorage.getItem('saints_token');
  
  // Fetch tiers
  useEffect(() => {
    fetchTiers();
  }, []);
  
  const fetchTiers = async () => {
    try {
      const response = await fetch('https://saintsacademy.co.za/api/v2/payments/tiers');
      const data = await response.json();
      setTiers(data);
      setMessage(`Loaded ${data.length} subscription plans`);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };
  
  const fetchStatus = async () => {
    if (!token) {
      setMessage('Please login to check status');
      return;
    }
    
    try {
      const response = await fetch('https://saintsacademy.co.za/api/v2/payments/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStatus(data);
      setMessage(`Current tier: ${data.currentTier || 'None'}`);
    } catch (error) {
      setMessage(`Status error: ${error.message}`);
    }
  };
  
  const handleFreeSubscription = async () => {
    if (!token) {
      setMessage('Please login first');
      return;
    }
    
    try {
      const response = await fetch('https://saintsacademy.co.za/api/v2/payments/subscribe/free', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setMessage(`✅ ${data.message}`);
      fetchStatus(); // Refresh status
    } catch (error) {
      setMessage(`❌ ${error.message}`);
    }
  };
  
  const handlePaymentSuccess = (result) => {
    setMessage(`✅ Payment successful! Tier: ${result.tier}, Amount: ${result.amount?.display}`);
    setShowPaymentForm(false);
    setSelectedTier(null);
    fetchStatus(); // Refresh status
  };
  
  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <h1>🧪 Payment Gateway Test</h1>
      
      {/* Message Display */}
      {message && (
        <div style={{ 
          padding: '15px', 
          marginBottom: '20px',
          borderRadius: '6px',
          background: message.includes('✅') ? '#d4edda' : 
                     message.includes('❌') ? '#f8d7da' : '#fff3cd',
          border: `1px solid ${message.includes('✅') ? '#c3e6cb' : 
                  message.includes('❌') ? '#f5c6cb' : '#ffeaa7'}`
        }}>
          {message}
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '30px', marginTop: '30px' }}>
        {/* Left: Tiers Selection */}
        <div style={{ flex: 2 }}>
          <h2>Select Plan</h2>
          
          <div style={{ margin: '20px 0' }}>
            <label style={{ marginRight: '20px' }}>
              <input
                type="radio"
                value="monthly"
                checked={billingCycle === 'monthly'}
                onChange={(e) => setBillingCycle(e.target.value)}
              /> Monthly
            </label>
            <label>
              <input
                type="radio"
                value="yearly"
                checked={billingCycle === 'yearly'}
                onChange={(e) => setBillingCycle(e.target.value)}
              /> Yearly
            </label>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px' 
          }}>
            {tiers.map(tier => (
              <div
                key={tier.id}
                style={{
                  border: `2px solid ${selectedTier?.id === tier.id ? '#6772e5' : '#e0e0e0'}`,
                  borderRadius: '10px',
                  padding: '25px',
                  background: selectedTier?.id === tier.id ? '#f8f9ff' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onClick={() => {
                  setSelectedTier(tier);
                  if (tier.name !== 'free') {
                    setShowPaymentForm(true);
                  }
                }}
              >
                <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>
                  {tier.display_name}
                </h3>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  {tier.description}
                </p>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#6772e5' }}>
                  R{billingCycle === 'yearly' ? tier.yearly_price : tier.monthly_price}
                  <span style={{ fontSize: '16px', color: '#666' }}>
                    /{billingCycle === 'yearly' ? 'year' : 'month'}
                  </span>
                </div>
                {tier.name === 'free' ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFreeSubscription();
                    }}
                    style={{
                      width: '100%',
                      marginTop: '20px',
                      padding: '12px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Get Free Plan
                  </button>
                ) : (
                  <div style={{
                    marginTop: '20px',
                    padding: '12px',
                    textAlign: 'center',
                    background: '#6c757d',
                    color: 'white',
                    borderRadius: '6px'
                  }}>
                    Click to select
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Right: Status & Actions */}
        <div style={{ flex: 1 }}>
          <h2>Account Status</h2>
          
          <div style={{ 
            padding: '20px', 
            background: '#f8f9fa', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <button
              onClick={fetchStatus}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '15px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh Status
            </button>
            
            {status && (
              <div>
                <p><strong>Current Tier:</strong> {status.currentTier || 'None'}</p>
                {status.subscription && (
                  <>
                    <p><strong>Status:</strong> {status.subscription.status}</p>
                    <p><strong>Amount:</strong> R{status.subscription.amount}</p>
                  </>
                )}
              </div>
            )}
          </div>
          
          <div style={{ padding: '20px', background: '#fff3cd', borderRadius: '8px' }}>
            <h4>Test Instructions</h4>
            <ol style={{ fontSize: '14px', lineHeight: '1.6' }}>
              <li>Ensure you're logged in</li>
              <li>Select a plan (Free or Paid)</li>
              <li>For paid plans: Use test card 4242 4242 4242 4242</li>
              <li>Check status after payment</li>
            </ol>
          </div>
        </div>
      </div>
      
      {/* Payment Form */}
      {showPaymentForm && selectedTier && (
        <div style={{ marginTop: '40px', padding: '30px', background: 'white', borderTop: '2px solid #eee' }}>
          <h2>Complete Payment for {selectedTier.display_name}</h2>
          <Elements stripe={stripePromise}>
            <PaymentForm
              selectedTier={selectedTier}
              billingCycle={billingCycle}
              onSuccess={handlePaymentSuccess}
              onCancel={() => {
                setShowPaymentForm(false);
                setSelectedTier(null);
              }}
            />
          </Elements>
        </div>
      )}
    </div>
  );
}

export default PaymentTest;